import { createRouter } from "@swan-io/chicane";
import { match } from "ts-pattern";
import { useEffect } from "react";
import { GridsPage } from "./pages/GridsPage";
import { GridsToValidatePage } from "./pages/GridsToValidatePage";
import { HomePage } from "./pages/HomePage";
import { EventPage } from "./pages/EventPage";
import { GamePage } from "./pages/GamePage";
import { DrawDisplayPage } from "./pages/DrawDisplayPage";
import { MembersPage } from "./pages/MembersPage";
import { PlayersPage } from "./pages/PlayersPage";
import { JoinOrganisationPage } from "./pages/JoinOrganisationPage";
import { CreateOrganisationPage } from "./pages/CreateOrganisationPage";
import { Layout } from "./components/Layout";
import { Spinner } from "./components/spinner";
import { AuthProvider, useAuth } from "./lib/auth";
import { OrganisationProvider, useOrganisation } from "./lib/organisation";
import { slugify } from "./lib/slugify";

// Define routes
export const Router = createRouter({
  Root: "/",
  Homepage: "/org/:orgSlug",
  CreateOrganisation: "/organisations/create",
  Grids: "/org/:orgSlug/grids",
  GridsToValidate: "/org/:orgSlug/grids/validate",
  Players: "/org/:orgSlug/players",
  Members: "/org/:orgSlug/members",
  JoinOrganisation: "/join/:code",
  Event: "/org/:orgSlug/event/:name/:id",
  Game: "/org/:orgSlug/game/:id",
  LiveDraw: "/live/:eventId",
});

function AppContent() {
  const { user, logout } = useAuth();
  const { organisations, currentOrganisation, setCurrentOrganisation, loading: orgLoading } = useOrganisation();
  const route = Router.useRoute(["Root", "Homepage", "CreateOrganisation", "Grids", "GridsToValidate", "Players", "Members", "JoinOrganisation", "Event", "Game", "LiveDraw"]);

  // Sync the current organisation with the URL slug
  useEffect(() => {
    if (orgLoading || organisations.length === 0) return;

    const orgSlug = getOrgSlugFromRoute(route);
    if (!orgSlug) return;

    const matchedOrg = organisations.find((org) => slugify(org.name) === orgSlug);
    if (matchedOrg && matchedOrg.id !== currentOrganisation?.id) {
      setCurrentOrganisation(matchedOrg);
    }
  }, [route, organisations, orgLoading, currentOrganisation?.id, setCurrentOrganisation]);

  if (orgLoading) {
    return <Spinner />;
  }

  if (route?.name === "JoinOrganisation") {
    return <JoinOrganisationPage code={route.params.code} />;
  }

  // Live draw page is public (no auth required for projection)
  if (route?.name === "LiveDraw") {
    return <DrawDisplayPage eventId={route.params.eventId} />;
  }

  if (organisations.length === 0) {
    return <CreateOrganisationPage />;
  }

  // Root "/" -> redirect to the current org's homepage
  if (!route || route.name === "Root") {
    const org = currentOrganisation ?? organisations[0]!;
    Router.replace("Homepage", { orgSlug: slugify(org.name) });
    return null;
  }

  const orgSlug = currentOrganisation ? slugify(currentOrganisation.name) : slugify(organisations[0]!.name);

  return (
    <Layout user={user} onLogout={logout}>
      {match(route)
        .with({ name: "Homepage" }, () => <HomePage />)
        .with({ name: "CreateOrganisation" }, () => <CreateOrganisationPage />)
        .with({ name: "Grids" }, () => <GridsPage />)
        .with({ name: "GridsToValidate" }, () => <GridsToValidatePage />)
        .with({ name: "Players" }, () => <PlayersPage />)
        .with({ name: "Members" }, () => <MembersPage />)
        .with({ name: "Event" }, ({ params }) => <EventPage name={params.name} id={params.id} />)
        .with({ name: "Game" }, ({ params }) => <GamePage id={params.id} />)
        .otherwise(() => {
          Router.replace("Homepage", { orgSlug });
          return null;
        })}
    </Layout>
  );
}

function getOrgSlugFromRoute(route: { params: Record<string, string> } | undefined | null): string | null {
  if (!route) return null;
  if ("orgSlug" in route.params) {
    return route.params.orgSlug ?? null;
  }
  return null;
}

export function App() {
  return (
    <AuthProvider>
      <OrganisationProvider>
        <AppContent />
      </OrganisationProvider>
    </AuthProvider>
  );
}

export default App;
